import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import usePageMeta from "../hooks/usePageMeta";
import { Button } from "../components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  ArrowLeft, TrendingUp, Zap, Timer, Target, LogOut, Sparkles,
} from "lucide-react";

const FUNNEL_STAGES = [
  { key: "applied", label: "Applied", color: "#5C6B64" },
  { key: "responded", label: "Response", color: "#8B9D83" },
  { key: "interviewed", label: "Interview", color: "#D99C3D" },
  { key: "offered", label: "Offer", color: "#1A362D" },
];

function Insight({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E5E1DB] p-5">
      <div className="flex items-center gap-2 text-[#5C6B64]">
        <Icon className="w-4 h-4" />
        <p className="text-xs tracking-[0.2em] uppercase font-semibold">{label}</p>
      </div>
      <p className="font-display text-4xl mt-3 text-[#1A362D]">{value}</p>
      {sub && <p className="text-sm text-[#5C6B64] mt-1">{sub}</p>}
    </div>
  );
}

export default function Analytics() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageMeta("Analytics · Trackr", "Response rate, funnel, and weekly velocity for your job search.");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/analytics");
        setData(data);
      } catch (err) {
        if (err.response?.status === 401) navigate("/login");
        else if (err.response?.status === 402) navigate("/upgrade");
        else toast.error("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    })();
     
  }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F1EE]">
        <div className="w-8 h-8 border-2 border-[#1A362D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div className="min-h-screen bg-[#F4F1EE]">
        <header className="glass sticky top-0 z-30">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#1A362D]" data-testid="back-to-dashboard-link">
              <ArrowLeft className="w-4 h-4" /> <span className="font-display text-lg">Trackr</span>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="rounded-full">
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-6 py-24 text-center">
          <p className="font-display text-4xl text-[#1A362D]">No data yet</p>
          <p className="text-[#5C6B64] mt-3">Add a few applications and Trackr will surface your response rates, funnel and velocity here.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-6 rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white" data-testid="analytics-back-btn">
            Back to dashboard
          </Button>
        </main>
      </div>
    );
  }

  const funnel = FUNNEL_STAGES.map((s) => ({
    ...s,
    count: data.funnel[s.key] ?? 0,
    pct: data.total ? Math.round((data.funnel[s.key] / data.total) * 100) : 0,
  }));
  const maxFunnel = Math.max(1, funnel[0].count);

  return (
    <div className="min-h-screen bg-[#F4F1EE]">
      <header className="glass sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#1A362D]" data-testid="back-to-dashboard-link">
            <ArrowLeft className="w-4 h-4" /> <span className="font-display text-lg">Trackr</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-[#5C6B64]">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="rounded-full" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B64]">Insights · Response performance</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 text-[#1A362D]">Your search, by the numbers.</h1>

        {data.insight && (
          <div className="mt-8 rounded-2xl bg-[#E2F0E7] border border-[#1A362D]/10 p-5 flex items-start gap-3" data-testid="insight-card">
            <Sparkles className="w-5 h-5 text-[#1A362D] shrink-0 mt-0.5" />
            <p className="text-[#1A362D]">{data.insight}</p>
          </div>
        )}

        {/* Headline metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <Insight
            icon={Target}
            label="Response rate"
            value={`${data.conversion.response_rate}%`}
            sub={`${data.funnel.responded} of ${data.total}`}
          />
          <Insight
            icon={TrendingUp}
            label="Interview rate"
            value={`${data.conversion.interview_rate}%`}
            sub={`${data.funnel.interviewed} interviews`}
          />
          <Insight
            icon={Zap}
            label="Offer rate"
            value={`${data.conversion.offer_rate}%`}
            sub={`${data.funnel.offered} offer${data.funnel.offered === 1 ? "" : "s"}`}
          />
          <Insight
            icon={Timer}
            label="Avg response"
            value={data.avg_response_days == null ? "—" : `${data.avg_response_days}d`}
            sub={data.avg_response_days == null ? "not enough data" : "from applied to first reply"}
          />
        </div>

        {/* Funnel */}
        <section className="mt-10 rounded-2xl bg-white border border-[#E5E1DB] p-6" data-testid="funnel-section">
          <h2 className="font-display text-2xl text-[#1A362D]">Funnel</h2>
          <p className="text-sm text-[#5C6B64] mt-1">Each stage as a share of your total applications.</p>
          <div className="mt-6 space-y-3">
            {funnel.map((s, i) => {
              const width = Math.max(6, Math.round((s.count / maxFunnel) * 100));
              const drop = i > 0 ? funnel[i - 1].count - s.count : 0;
              return (
                <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="font-medium text-[#1A362D]">{s.label}</span>
                    <span className="text-[#5C6B64]">
                      {s.count}
                      <span className="mx-2 text-[#E5E1DB]">·</span>
                      {s.pct}%
                    </span>
                  </div>
                  <div className="mt-1.5 h-8 w-full rounded-lg bg-[#F4F1EE] overflow-hidden">
                    <div
                      className="h-full rounded-lg transition-all"
                      style={{ width: `${width}%`, backgroundColor: s.color }}
                    />
                  </div>
                  {i > 0 && drop > 0 && (
                    <p className="text-xs text-[#C65D47] mt-1">-{drop} dropped from {funnel[i - 1].label}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-6">
          {/* Velocity chart */}
          <section className="lg:col-span-3 rounded-2xl bg-white border border-[#E5E1DB] p-6" data-testid="velocity-section">
            <h2 className="font-display text-2xl text-[#1A362D]">Weekly velocity</h2>
            <p className="text-sm text-[#5C6B64] mt-1">Applications submitted per week (last 12 weeks).</p>
            <div className="mt-6 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.velocity} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                  <CartesianGrid stroke="#F0EDE7" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: "#5C6B64", fontSize: 11 }}
                    axisLine={{ stroke: "#E5E1DB" }}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "#5C6B64", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(26,54,45,0.06)" }}
                    contentStyle={{
                      background: "#1A362D",
                      border: "none",
                      borderRadius: 8,
                      color: "#F4F1EE",
                      fontSize: 12,
                    }}
                    labelStyle={{ color: "#D99C3D" }}
                  />
                  <Bar dataKey="count" fill="#1A362D" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Source performance */}
          <section className="lg:col-span-2 rounded-2xl bg-white border border-[#E5E1DB] p-6" data-testid="source-section">
            <h2 className="font-display text-2xl text-[#1A362D]">By source</h2>
            <p className="text-sm text-[#5C6B64] mt-1">Which channels actually reply.</p>
            <div className="mt-4 -mx-2">
              {data.by_source.length === 0 ? (
                <p className="text-sm text-[#5C6B64] px-2">Add source info to your applications to see this.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs tracking-[0.15em] uppercase text-[#5C6B64]">
                      <th className="px-2 py-2">Source</th>
                      <th className="px-2 py-2 text-right">N</th>
                      <th className="px-2 py-2 text-right">Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_source.slice(0, 8).map((s) => (
                      <tr key={s.source} className="border-t border-[#F0EDE7]" data-testid={`source-row-${s.source}`}>
                        <td className="px-2 py-2.5 text-[#1A362D] font-medium">{s.source}</td>
                        <td className="px-2 py-2.5 text-right text-[#5C6B64]">{s.count}</td>
                        <td className="px-2 py-2.5 text-right">
                          <div className="inline-flex items-center gap-2">
                            <div className="w-16 h-1.5 rounded-full bg-[#F4F1EE] overflow-hidden">
                              <div className="h-full rounded-full bg-[#1A362D]"
                                style={{ width: `${Math.min(100, s.response_rate)}%` }} />
                            </div>
                            <span className="font-medium text-[#1A362D] tabular-nums">{s.response_rate}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>

        {/* Current pipeline snapshot */}
        <section className="mt-6 rounded-2xl bg-white border border-[#E5E1DB] p-6" data-testid="pipeline-section">
          <h2 className="font-display text-2xl text-[#1A362D]">Current pipeline</h2>
          <p className="text-sm text-[#5C6B64] mt-1">Where your applications sit today.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {[
              { key: "applied", label: "Applied", tint: "#5C6B64" },
              { key: "interviewing", label: "Interviewing", tint: "#D99C3D" },
              { key: "offer", label: "Offer", tint: "#1A362D" },
              { key: "rejected", label: "Rejected", tint: "#C65D47" },
            ].map((s) => (
              <div key={s.key} className="rounded-xl bg-[#FDFCFA] border border-[#F0EDE7] p-4" data-testid={`pipeline-${s.key}`}>
                <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B64]">{s.label}</p>
                <p className="font-display text-3xl mt-2" style={{ color: s.tint }}>
                  {data.current_status[s.key]}
                </p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
